import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Mail, Shield } from "lucide-react";
import { COMPANY, LOGO_FOOTER_URL } from "../lib/constants";

const SEO_PRO_ANCHORS = [
  { label: "Serrurier hôtel Saint-Malo", to: "/serrurier-hoteliers" },
  { label: "Serrurier hôtel Dinard", to: "/serrurier-hoteliers" },
  { label: "Serrurier hôtel Cancale", to: "/serrurier-hoteliers" },
  { label: "Serrurier syndic Saint-Malo", to: "/serrurier-syndics-coproprietes" },
  { label: "Serrurier syndic Dinard", to: "/serrurier-syndics-coproprietes" },
  { label: "Serrurier copropriété Dinan", to: "/serrurier-syndics-coproprietes" },
  { label: "Serrurier agence Saint-Malo", to: "/serrurier-agences-immobilieres" },
  { label: "Serrurier agence Dinard", to: "/serrurier-agences-immobilieres" },
  { label: "Serrurier bailleur 22 35", to: "/serrurier-agences-immobilieres" },
  { label: "Serrurier conciergerie Saint-Malo", to: "/serrurier-conciergeries" },
  { label: "Serrurier conciergerie Dinard", to: "/serrurier-conciergeries" },
  { label: "Serrurier Airbnb Côte d'Émeraude", to: "/serrurier-conciergeries" },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-slate-900 text-slate-200 mt-24"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2 space-y-4">
          <div className="flex items-center gap-3">
            <img
              src={LOGO_FOOTER_URL}
              alt="Mon Serrurier Breton"
              className="h-20 md:h-24 w-auto object-contain"
              data-testid="footer-logo"
            />
          </div>
          <p className="text-sm text-slate-400 max-w-md leading-relaxed">
            Artisan serrurier diplômé, basé à Mont-Dol (35120).
            Intervention rapide et soignée, tarifs transparents, travail
            garanti. Particuliers & professionnels.
          </p>
          <div className="inline-flex items-center gap-2 bg-teal-900/30 border border-teal-700/40 text-teal-300 rounded-full px-4 py-1.5 text-xs font-semibold">
            <Shield className="w-3.5 h-3.5" /> Remboursement assurance sur
            facture
          </div>
        </div>

        <div>
          <div className="font-display font-bold text-white mb-4">
            Navigation
          </div>
          <ul className="space-y-2 text-sm">
            <li>
              <Link to="/" className="hover:text-teal-300" data-testid="footer-link-accueil">
                Accueil
              </Link>
            </li>
            <li>
              <Link to="/services" className="hover:text-teal-300" data-testid="footer-link-services">
                Nos Services
              </Link>
            </li>
            <li>
              <Link to="/urgence" className="hover:text-teal-300" data-testid="footer-link-urgence">
                Dépannage / Urgence
              </Link>
            </li>
            <li>
              <Link to="/professionnels" className="hover:text-teal-300" data-testid="footer-link-pro">
                Professionnels
              </Link>
            </li>
            <li>
              <Link to="/contact" className="hover:text-teal-300" data-testid="footer-link-contact">
                Contact
              </Link>
            </li>
            <li>
              <Link
                to="/zones-intervention"
                className="hover:text-teal-300"
                data-testid="footer-link-zones"
              >
                Zones d'intervention
              </Link>
            </li>
            <li>
              <Link to="/faq" className="hover:text-teal-300" data-testid="footer-link-faq">
                FAQ
              </Link>
            </li>
            <li>
              <Link
                to="/charte-transparence"
                className="hover:text-teal-300"
                data-testid="footer-link-charte"
              >
                Charte anti-arnaque
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <div className="font-display font-bold text-white mb-4">Contact</div>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <Phone className="w-4 h-4 mt-0.5 text-amber-400" />
              <a
                href={`tel:${COMPANY.phoneDigits}`}
                className="hover:text-amber-400 font-semibold"
                data-testid="footer-phone"
              >
                {COMPANY.phone}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Clock className="w-4 h-4 mt-0.5 text-teal-300" />
              <span>24h/24 — 7j/7</span>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 text-teal-300" />
              <span>Mont-Dol & alentours (35 · 22)</span>
            </li>
            <li className="flex items-start gap-2">
              <Mail className="w-4 h-4 mt-0.5 text-teal-300" />
              <span className="break-all">{COMPANY.email}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-teal-300 mb-4">
            Serrurerie pro · zones les plus demandées
          </div>
          <div className="flex flex-wrap gap-x-1.5 gap-y-1.5 text-[11px] text-slate-400">
            {SEO_PRO_ANCHORS.map((a, i) => (
              <span key={a.label} className="flex items-center gap-1.5">
                <Link
                  to={a.to}
                  data-testid={`footer-seo-anchor-${i + 1}`}
                  className="hover:text-teal-300 hover:underline whitespace-nowrap"
                >
                  {a.label}
                </Link>
                {i < SEO_PRO_ANCHORS.length - 1 && (
                  <span className="text-slate-600">·</span>
                )}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex flex-col gap-3 text-xs text-slate-500">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>© {new Date().getFullYear()} Mon Serrurier Breton — Artisan diplômé.</div>
            <div className="flex flex-wrap items-center gap-4 md:gap-6">
              <Link
                to="/mentions-legales"
                className="hover:text-teal-300"
                data-testid="footer-bottom-mentions"
              >
                Mentions légales
              </Link>
              <span>Devis gratuit</span>
              <span>Travail garanti</span>
              <span>Intervention 30 min</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-slate-400">
            <span data-testid="footer-siret">SIRET : {COMPANY.siret}</span>
            <span aria-hidden>·</span>
            <span data-testid="footer-insurance">{COMPANY.insurance}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
